import { ScatterplotLayer } from "@deck.gl/layers";
import { Position } from "geojson";
import { MapLayerMouseEvent } from "maplibre-gl";
import { useMemo } from "react";

import { DrawLinePointPolygonProps } from "../types/draw-line-point-polygon-props";
import { DEFAULT_POINT_LAYER_PROPS } from "../utils/map-draw-consts";

export const useDrawPoint = ({
  active,
  setRemovePopup,
  isDeleting,
  acceptedGeometryTypes,
  formData,
  onChange,
}: DrawLinePointPolygonProps) => {
  // Derive points from parent state
  const points: Position[] = useMemo(() => {
    if (formData) {
      if (formData.type === "Point" && formData.coordinates.length) return [formData.coordinates];
      if (formData.type === "MultiPoint") return formData.coordinates;
    }
    return [];
  }, [formData]);

  const onMapClick = (e: MapLayerMouseEvent) => {
    const mouseLocation = e.lngLat.toArray();
    if (!active || isDeleting) return;

    // If no formData, start with a Point
    if (!formData || !acceptedGeometryTypes.includes("MultiPoint")) {
      onChange({
        type: "Point",
        coordinates: mouseLocation,
      });
      return;
    }

    // If formData is a Point, convert to MultiPoint unless it was empty
    if (formData.type === "Point") {
      if (!formData.coordinates.length) {
        onChange({
          type: "Point",
          coordinates: mouseLocation,
        });
        return;
      }
      onChange({
        type: "MultiPoint",
        coordinates: [formData.coordinates, mouseLocation],
      });
      return;
    }

    // If formData is a MultiPoint, add new polygon
    if (formData.type === "MultiPoint") {
      onChange({
        type: "MultiPoint",
        coordinates: [...formData.coordinates, mouseLocation],
      });
      return;
    }
  };

  const pointLayer = new ScatterplotLayer({
    ...DEFAULT_POINT_LAYER_PROPS,
    id: "POINT-existing-point-layer",
    pickable: isDeleting,
    onClick: ({ object, index }) => {
      setRemovePopup({
        coordinates: object,
        type: "Point",
        onConfirm: () => {
          if (!formData) return;
          if (formData.type === "Point") {
            onChange({ type: "Point", coordinates: [] });
            return;
          }
          if (formData.type === "MultiPoint") {
            const pointsCopy = [...points];
            pointsCopy.splice(index, 1);

            if (pointsCopy.length > 1) {
              onChange({
                type: "MultiPoint",
                coordinates: pointsCopy,
              });
              return;
            }

            onChange({ type: "Point", coordinates: pointsCopy[0] });
          }
        },
      });
    },
    data: points,
  });

  return {
    pointLayer,
    points,
    onMapClick,
  };
};
